/**
 * useAnomalyAlerts — list anomaly_alerts visible to the current user.
 *
 * RLS scopes the visibility: super_admin sees every org; supervisor /
 * contractor_admin see all in their org; cleaner / maintenance /
 * client_view have no SELECT policy and the hook returns empty.
 *
 * Default filter: open + recent reviewed in the same query so the page
 * can show "active alerts" + "recently triaged" together. The page
 * splits them client-side; this hook keeps one round-trip.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'

export type AnomalyCategory =
  | 'repeat_redo'
  | 'off_hours_approval'
  | 'transition_failure_cluster'

export type AnomalySeverity = 'low' | 'medium' | 'high'

export type AnomalyStatus = 'open' | 'confirmed' | 'dismissed'

export interface AnomalyAlertRow {
  id:                       string
  organization_id:          string
  category:                 AnomalyCategory
  severity:                 AnomalySeverity
  related_entity_type:      'room' | 'user'
  related_entity_id:        string
  detection_window_start:   string
  detection_window_end:     string
  metrics_json:             Record<string, unknown>
  ai_summary:               string | null
  status:                   AnomalyStatus
  reviewed_by_user_id:      string | null
  reviewed_at:              string | null
  reviewer_notes:           string | null
  created_at:               string
}

const ANOMALY_LIST_LIMIT = 200

export function useAnomalyAlerts(): UseQueryResult<AnomalyAlertRow[]> {
  const supabase = useSupabase()
  return useQuery({
    queryKey: ['anomaly-alerts'],
    queryFn: async (): Promise<AnomalyAlertRow[]> => {
      const { data, error } = await supabase
        .from('anomaly_alerts')
        .select(
          'id, organization_id, category, severity, related_entity_type, related_entity_id,' +
          ' detection_window_start, detection_window_end, metrics_json, ai_summary,' +
          ' status, reviewed_by_user_id, reviewed_at, reviewer_notes, created_at',
        )
        .order('status',     { ascending: true })   // 'open' (alphabetically first of the three) leads
        .order('created_at', { ascending: false })
        .limit(ANOMALY_LIST_LIMIT)
      if (error) throw error
      return (data ?? []) as unknown as AnomalyAlertRow[]
    },
    refetchInterval: 60_000,
  })
}

export const SEVERITY_RANK: Readonly<Record<AnomalySeverity, number>> = {
  high:   0,
  medium: 1,
  low:    2,
}
